import { useMemo } from "react";
import AssignToManager from "./AssignToManager";
import SelectMember from "./SelectMember";
import { useAuth } from "../contexts/AuthContext";
import type { Template } from "../api/endpoints/fetchTemplates";
import { useAcroFormTemplateDetails } from "../hooks/useAcroFormTemplateDetails";
import { buildSectionsFromAcro } from "../data/acroHelper";
import FormInfoHeader from "./FormInfoHeader";
import FormStructureSummary from "./FormStructureSummary";
import SectionsPreview, { SectionPreviewItem } from "./SectionsPreview";

interface AcroFormInfoPanelProps {
  template: Template;
}

function countEditableFields(sectionHtml: string): number {
  const container = document.createElement("div");
  container.innerHTML = sectionHtml;
  const inputs = container.querySelectorAll("input, textarea, select");
  let count = 0;
  inputs.forEach((el) => {
    if (
      el instanceof HTMLInputElement ||
      el instanceof HTMLTextAreaElement ||
      el instanceof HTMLSelectElement
    ) {
      // consider all standard form controls as editable
      count += 1;
    }
  });
  return count;
}

export function AcroFormInfoPanel({ template }: AcroFormInfoPanelProps) {
  const { isAdmin } = useAuth();


  const { data, isLoading, isError, error } = useAcroFormTemplateDetails({
    templateId: template.fileId,
    userEmail: template.userEmail
  });

  const built = useMemo(() => {
    if (!data?.json) return null;
    return buildSectionsFromAcro(data.json);
  }, [data?.json]);

  // Exclude synthetic header step from section counts and previews
  const sections = (built?.sections ?? []).filter((s) => s.id !== "__header__");
  const sectionCount = sections.length;
  const subsectionCount = 0;
  // If there are no sections, header/groups HTML may still contain fields. Include them.
  const headerAndGroupsHtml = built?.headersHtml ?? "";
  const totalFields = sections.reduce((acc, s) => acc + countEditableFields(s.html), 0)
    + (sectionCount === 0 ? countEditableFields(headerAndGroupsHtml) : 0);
  const estimatedTime = Math.max(1, Math.ceil(totalFields * 0.5));

  const previewItems: SectionPreviewItem[] = useMemo(() => {
    return sections.map((s, idx) => ({ id: String(idx), name: s.name, subsections: [] }));
  }, [sections]);

  const templateTitle = template.fileTitle || template.fileName || "Untitled Template";
  const templateDetailsErrorMessage = error && typeof error === "object" && "message" in error ? String((error as { message?: unknown }).message ?? "") : undefined;
  const structureIsLoading = isLoading;
  const structureErrorMessage = isError ? (templateDetailsErrorMessage || "") : undefined;

  return (
    <div className="acro-form-info-panel flex flex-col h-full">
      <div className="p-6 space-y-6 flex-1 overflow-y-auto">
        <FormInfoHeader
          title={templateTitle}
          published={template.published === "Published"}
          estimatedTime={estimatedTime}
          isLoadingEstimatedTime={isLoading}
          estimatedTimeErrorMessage={templateDetailsErrorMessage}
        />

        <div className="mt-8">
          {/* Only show if template has htmlId (required for assignment) */}
          {template.htmlId && (
            <>
              {/* Show AssignToManager if admin and published */}
              {isAdmin && template.isPublished && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="w-1 h-4 rounded-full" style={{ backgroundColor: "var(--color-primary)" }}></span>
                    Assign to Manager
                  </h4>
                  <AssignToManager htmlId={template.htmlId} />
                </div>
              )}
              {/* Show SelectMember if not admin */}
              {!isAdmin && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <span className="w-1 h-4 rounded-full" style={{ backgroundColor: "var(--color-primary)" }}></span>
                    Assign to Member
                  </h4>
                  <SelectMember
                    htmlId={template.htmlId}
                    userId={template.memberId}
                    disabled={!!template.memberId}
                    onMemberAssigned={() => { }}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div>
          <FormStructureSummary
            isLoading={structureIsLoading}
            errorMessage={structureErrorMessage}
            sectionCount={sectionCount}
            subsectionCount={subsectionCount}
            fieldCount={totalFields}
          />
        </div>

        <div>
          <SectionsPreview
            isLoading={structureIsLoading}
            errorMessage={structureErrorMessage}
            items={previewItems}
          />
        </div>
      </div>
    </div>
  );
}

export default AcroFormInfoPanel;
